// src/components/ProfileForm.jsx
import { useState, useEffect, useMemo } from 'react';
import { IMaskInput } from 'react-imask';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import { useUser } from '../contexts/UserContext';
import { useOrder } from '../contexts/OrderContext';
import Spinner from './Spinner';
import { calculateGoalWeight, calculateEstimatedGoalDate, useWeightMetrics, calculateBMI } from '../utilities/weightCalculations';


// --- Constants ---
const inputClassName = "mt-1 block w-full p-2 border border-neutral rounded focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-gray-100 disabled:cursor-not-allowed text-sm";
const labelClassName = "block text-sm font-medium text-gray-600 mb-1";
const prefixOptions = ['', 'Mr', 'Mrs', 'Ms', 'Miss', 'Dr'];
const defaultHeightInches = "69";
const healthProfileFields = ['height', 'weight', 'goal_weight', 'goal_date', 'start_date', 'bmi'];
const socialHistoryFields = ['alcohol_oz', 'cups_day', 'exercise'];

function ProfileForm({ onSaveComplete, onSaveSuccess, onDirty }) {
  const userContext = useUser();
  const orderContext = useOrder();

	const {
		bmi,
		averageLossPerWeek,
		projectedGoalDate,
		startDate,
		currentWeight
	} = useWeightMetrics(
		userContext.user,
		orderContext.orders,
		orderContext.orderHistory
	);

  const heightOptions = useMemo(() => {
    const options = [];
    for (let i = 48; i <= 90; i++) {
      const feet = Math.floor(i / 12);
      const inches = i % 12;
      options.push({ value: i.toString(), display: `${feet}' ${inches}"` });
    }	
    return options;
  }, []);	

  const userid = userContext?.user?.userid;
  const [formData, setFormData] = useState({
    prefix: '', first_name: '', middle_name: '', last_name: '', suffix: '',
    gender: '', marital_status: '', height: defaultHeightInches, weight: '',
    personal_physician: '', physician_number: '',
    alcohol_oz: '', cups_day: '', exercise: '',
    birthdate: '', start_date: '', goal_weight: '', goal_date: '', bmi: 0.0
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!userid || !userContext.user) return;

    let missing = [];
    const user = userContext.user;

		// use our hook values
		let goalWeight  = user.goal_weight   || calculateGoalWeight(user.height, 25);
		let goalDate    = user.goal_date     || projectedGoalDate;
		let effectiveStart = user.start_date && user.start_date !== '0000-00-00'
     ? user?.start_date
     : format(startDate, 'yyyy-MM-dd');

		if (effectiveStart === format(new Date(), 'yyyy-MM-dd') && !user.start_date) {
      missing.push('Start date');
    }

    if (!user?.goal_weight) {
      missing.push('Goal weight');
    }

    if (!user.goal_date) {
			missing.push('Goal date');
			goalDate = projectedGoalDate;
    }

    setFormData({
      prefix: user.prefix || '',
      first_name: user.first_name || '',
      middle_name: user.middle_name || '',
      last_name: user.last_name || '',
      suffix: user.suffix || '',
      gender: user.gender || '',
      marital_status: user.marital_status || '',
      height: user.height || defaultHeightInches,
      weight: user.weight || '',
      personal_physician: user.personal_physician || '',
      physician_number: user.physician_number || '',
      alcohol_oz: String(user.alcohol_oz || ''),
      cups_day: String(user.cups_day || ''),
      exercise: user.exercise || '',
      birthdate: user.birthdate || '',
      start_date: effectiveStart,
      goal_weight: goalWeight,
      goal_date: goalDate,
      bmi: bmi,
    });

    if (missing.length > 0) {
      toast.error(`Please update your ${missing.join(', ')} in your profile.`, { duration: 10000 });
    }

    setIsLoading(false);
  }, [userid, userContext.user, bmi, averageLossPerWeek, projectedGoalDate]);


  const handleChange = (e) => {
    const { name, value } = e.target;
    onDirty?.();
    if (['weight', 'goal_weight'].includes(name) && (value === '' || /^\d*\.?\d*$/.test(value))) {
      setFormData(prev => ({ ...prev, [name]: value }));
    } else if (['alcohol_oz', 'cups_day'].includes(name) && (value === '' || /^\d*$/.test(value))) {
      setFormData(prev => ({ ...prev, [name]: value }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    if (['weight', 'height'].includes(name)) {
      setFormData(prev => ({ ...prev, bmi: calculateBMI(prev.height, prev.weight) }));
    }
  };

	// Helper function to calculate goal date - used by both Recalculate button and Save
	// Returns { success: boolean, goalDate: string|null, error: string|null }
	const calculateGoalDateFromInputs = (startDateInput, goalWeightInput) => {
		// 1) Determine start date: prefer input, else hook value
		const sd = startDateInput
			? new Date(startDateInput)      // assume YYYY-MM-DD
			: startDate;                    // from useWeightMetrics hook

		if (!sd || isNaN(sd.getTime())) {
			return { success: false, goalDate: null, error: 'Invalid start date.' };
		}

		// 2) Determine goal weight: input or 25-BMI fallback
		let gw = parseFloat(goalWeightInput);
		if (isNaN(gw) || gw <= 0) {
			gw = parseFloat(calculateGoalWeight(userContext.user.height, 24));
		}

		// 3) Project new goal date
		const newGoalDate = calculateEstimatedGoalDate(
			sd,
			currentWeight,
			gw,
			averageLossPerWeek,
			false   // project from sd, not "today"
		);

		if (!newGoalDate) {
			return { success: false, goalDate: null, error: 'Unable to calculate goal date.' };
		}

		return { success: true, goalDate: newGoalDate, startDate: format(sd, 'yyyy-MM-dd'), goalWeight: gw.toString(), error: null };
	};

	const handleRecalculateGoalDate = () => {
		setError('');
    onDirty?.();
		const result = calculateGoalDateFromInputs(formData.start_date, formData.goal_weight);

		if (!result.success) {
			setError(result.error);
			return;
		}

		// Write it back to form
		setFormData(prev => ({
			...prev,
			start_date: result.startDate,
			goal_weight: result.goalWeight,
			goal_date: result.goalDate
		}));
	};

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // FIX (2026-01-26): Automatically recalculate goal_date on save if we have the required data.
    // This ensures the target goal date is always up-to-date based on current progress.
    let updatedFormData = { ...formData };
    if (averageLossPerWeek > 0 && currentWeight > 0) {
      const result = calculateGoalDateFromInputs(formData.start_date, formData.goal_weight);
      if (result.success) {
        updatedFormData = {
          ...updatedFormData,
          start_date: result.startDate,
          goal_weight: result.goalWeight,
          goal_date: result.goalDate
        };
      }
    }

    const userData = {};
    const healthData = {};
    const socialData = {};

    Object.keys(updatedFormData).forEach(key => {
      if (healthProfileFields.includes(key)) {
        healthData[key] = ['weight', 'goal_weight', 'bmi'].includes(key) ? parseFloat(updatedFormData[key]) : updatedFormData[key];
      } else if (socialHistoryFields.includes(key)) {
        socialData[key] = updatedFormData[key] || '';
      } else {
        userData[key] = updatedFormData[key];
      }
    });

    if (Object.keys(socialData).length > 0) {
      healthData.social_history = socialData;
    }

    //bundle the update calls
    const [userResult, healthResult] = await Promise.all([
      userContext.updateUserInfo(userData),
      userContext.updateHealthProfile(healthData),
    ]);

    if (userResult && healthResult) {
      // Update local form state with recalculated values
      setFormData(updatedFormData);
      toast.success("Profile updated successfully");
      onSaveSuccess?.();
    } else {
      setError("One or more profile updates failed.");
    }

    setIsSubmitting(false);
  };

  if (userContext?.loadingSession || isLoading) {
    return <div className="h-64 flex items-center justify-center"><Spinner /></div>;
  }

  return (
    // Outer container: Takes full height from parent
    <div className="h-full">
        {/* Form: flex column, takes full height, adds space between content and button */}
        <form onSubmit={handleSubmit} className="flex flex-col h-full space-y-4">
            {/* Content Wrapper: Scrolls if needed, ADD flex-grow */}
            <div className="flex-grow space-y-4 pb-4">
                {/* --- Name Section (Single Line) --- */}
                <fieldset className="border p-4 rounded mt-4" disabled={isSubmitting}>
                    <legend className="text-md font-medium text-primary px-1 -mb-2">Name</legend>
                    <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
                         <div className="flex-none w-20">
                            <label htmlFor="prefix" className={labelClassName}>Prefix</label>
                            <select id="prefix" name="prefix" value={formData.prefix} onChange={handleChange} className={`${inputClassName} bg-white`}>
                                {prefixOptions.map(p => <option key={p} value={p}>{p || '-'}</option>)}
                            </select>
                        </div>
                        <div className="flex-1 min-w-[120px]">
                            <label htmlFor="first_name" className={labelClassName}>First *</label>
                            <input type="text" id="first_name" name="first_name" value={formData.first_name} onChange={handleChange} className={inputClassName} required />
                        </div>
                        <div className="flex-none w-24">
                            <label htmlFor="middle_name" className={labelClassName}>Middle</label>
                            <input type="text" id="middle_name" name="middle_name" value={formData.middle_name} onChange={handleChange} className={inputClassName} />
                        </div>
                        <div className="flex-1 min-w-[120px]">
                            <label htmlFor="last_name" className={labelClassName}>Last *</label>
                            <input type="text" id="last_name" name="last_name" value={formData.last_name} onChange={handleChange} className={inputClassName} required />
                        </div>
                        <div className="flex-none w-20">
                            <label htmlFor="suffix" className={labelClassName}>Suffix</label>
                            <select id="suffix" name="suffix" value={formData.suffix} onChange={handleChange} className={`${inputClassName} bg-white`}>
                                <option value="">-</option> <option value="Sr">Sr</option> <option value="Jr">Jr</option> <option value="II">II</option> <option value="III">III</option> <option value="IV">IV</option> <option value="V">V</option>
                            </select>
                        </div>
                    </div>
                </fieldset>

                {/* --- Demographics Section (Single Line) --- */}
                <fieldset className="border p-4 rounded" disabled={isSubmitting}>
                   <legend className="text-md font-medium text-primary px-1 -mb-2">About You</legend>
                    <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
                        <div className="flex-1 min-w-[130px]">
                            <label htmlFor="birthdate" className={labelClassName}>Birthdate *</label>
                            <input type="date" id="birthdate" name="birthdate" value={formData.birthdate} onChange={handleChange} className={`${inputClassName} bg-white`} required/>
                        </div>
                        <div className="flex-1 min-w-[100px]">
                            <label htmlFor="gender" className={labelClassName}>Gender *</label>
                            <select id="gender" name="gender" value={formData.gender} onChange={handleChange} className={`${inputClassName} bg-white`} required>
                                <option value="" disabled>Select</option> <option value="m">Male</option> <option value="f">Female</option>
                            </select>
                        </div>
                        <div className="flex-1 min-w-[120px]">
                            <label htmlFor="marital_status" className={labelClassName}>Marital Status</label>
                            <select id="marital_status" name="marital_status" value={formData.marital_status} onChange={handleChange} className={`${inputClassName} bg-white`}>
                                <option value="">Select</option> <option value="single">Single</option> <option value="married">Married</option> <option value="divorced">Divorced</option> <option value="widowed">Widowed</option> <option value="separated">Separated</option>
                            </select>
                        </div>
                        <div className="flex-none w-28">
                            <label htmlFor="height" className={labelClassName}>Height *</label>
                            <select id="height" name="height" value={formData.height} onChange={handleChange} className={`${inputClassName} bg-white`} required>
                                {Array.isArray(heightOptions) && heightOptions.map(option => (
                                    <option key={option.value} value={option.value}>{option.display}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex-none w-28">
                            <label htmlFor="weight" className={labelClassName}>Weight (lbs) *</label>
                            <input type="text" inputMode="numeric" id="weight" name="weight" value={formData.weight} onChange={handleChange} className={inputClassName} required placeholder="e.g., 185" />
                        </div>
                        <div className="flex-none w-28">
                            <label htmlFor="bmi_display" className={labelClassName}>Current BMI</label>
                            {/* Display BMI from the hook */}
                            <input type="text" id="bmi_display" name="bmi_display" value={formData.bmi ?? 'N/A'} readOnly className={`${inputClassName} bg-gray-100`} />
                        </div>
                    </div>
                </fieldset>

                {/* --- Physician & Lifestyle Section (Single Line) --- */}
                <fieldset className="border p-4 rounded" disabled={isSubmitting}>
                    <legend className="text-md font-medium text-primary px-1 -mb-2">Physician & Lifestyle</legend>
                    <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
                        <div className="flex-1 min-w-[150px]">
                            <label htmlFor="personal_physician" className={labelClassName}>Primary Physician</label>
                            <input type="text" id="personal_physician" name="personal_physician" value={formData.personal_physician} onChange={handleChange} className={inputClassName} />
                        </div>
                        <div className="flex-none w-36">
                            <label htmlFor="physician_number" className={labelClassName}>Physician's Phone</label>
                            <IMaskInput mask="000-000-0000" unmask={false} id="physician_number" name="physician_number" value={formData.physician_number} onAccept={(value) => handleChange({ target: { name: 'physician_number', value } })} placeholder="___-___-____" className={inputClassName} type="tel" />
                        </div>
                        <div className="flex-none w-32">
                            <label htmlFor="alcohol_oz" className={labelClassName}>Alcohol/Week</label>
                            <input type="text" inputMode="numeric" id="alcohol_oz" name="alcohol_oz" value={formData.alcohol_oz} onChange={handleChange} className={inputClassName} placeholder="e.g., 0" />
                        </div>
                        <div className="flex-none w-32">
                            <label htmlFor="cups_day" className={labelClassName}>Coffee/Tea/Day</label>
                            <input type="text" inputMode="numeric" id="cups_day" name="cups_day" value={formData.cups_day} onChange={handleChange} className={inputClassName} placeholder="e.g., 2" />
                        </div>
                        <div className="flex-none w-28">
                            <label htmlFor="exercise" className={labelClassName}>Exercise?</label>
                            <select id="exercise" name="exercise" value={formData.exercise} onChange={handleChange} className={`${inputClassName} bg-white`}>
                                <option value="">Select</option> <option value="yes">Yes</option> <option value="no">No</option>
                            </select>
                        </div>
                    </div>
                </fieldset>

                {/* --- Weightloss Section(Single Line) --- */}
                <fieldset className="border p-4 rounded" disabled={isSubmitting}>
                    <legend className="text-md font-medium text-primary px-1 -mb-2">Weightloss</legend>
                    <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
                        <div className="flex-1 min-w-[120px]">
                            <label htmlFor="start_date" className={labelClassName}>Calculate Avg From:</label>
                            {/* Make Start Date Editable */}
                            <input type="date" id="start_date" name="start_date" value={formData.start_date} onChange={handleChange} className={`${inputClassName} bg-white`} />
                        </div>
                        <div className="flex-1 min-w-[120px]">
                            <label htmlFor="avg_loss_display" className={labelClassName}>Avg. Loss</label>
                            {/* Display Avg Loss from the hook */}
                            <input type="text" id="avg_loss_display" name="avg_loss_display" value={averageLossPerWeek != null ? `${averageLossPerWeek.toFixed(1)} lbs/wk` : 'N/A'} readOnly className={`${inputClassName} bg-gray-100`} />
                        </div>
                        <div className="flex-1 min-w-[120px]">
                            <label htmlFor="goal_weight" className={labelClassName}>Goal Weight (lbs)</label>
                            <input type="text" inputMode="numeric" id="goal_weight" name="goal_weight" value={formData.goal_weight} onChange={handleChange} placeholder="e.g., 160" className={inputClassName} />
                        </div>
                        <div className="flex-1 min-w-[120px]">
                            <label htmlFor="goal_date" className={labelClassName}>Target Goal Date</label>
                            <input type="date" id="goal_date" name="goal_date" value={formData.goal_date} onChange={handleChange} className={`${inputClassName} bg-white`} placeholder="Date" />
                        </div>
                        <div className="flex-none self-end">
                            <button
                                type="button"
                                onClick={handleRecalculateGoalDate}
                                className="bg-secondary hover:bg-secondary-light text-primary-dark px-3 py-2 rounded text-sm"
                                title="Recalculate target date based on current weight and goal"
                                disabled={isSubmitting}
                            >
                                Recalculate
                            </button>
                        </div>
                    </div>
                </fieldset>
            </div> {/* End scrollable content wrapper */}

            {/* --- Submission Area --- */}
            <div className="flex flex-col sm:flex-row justify-end items-stretch sm:items-center gap-3 pb-8">
                {error && <p className="text-red-500 text-sm mb-2">{error}</p>}
                <button
                    type="button"
                    onClick={onSaveComplete}
                    className={`w-full sm:w-1/4 bg-neutral-light text-neutral-dark p-3 rounded hover:bg-neutral-dark hover:text-white transition duration-200 font-semibold ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? '' : 'Close'}
                </button>
                <button
                    type="submit"
                    className={`w-full sm:w-1/4 bg-button text-white p-3 rounded hover:bg-button-light transition duration-200 font-semibold ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? 'Saving...' : 'Save Profile Info'}
                </button>
            </div>
        </form>
    </div>
  );
}
export default ProfileForm;
